import { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { authAPI, setAccessToken } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from '../components/shared/index';

export default function VerifyEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  const [status, setStatus]   = useState('verifying'); // verifying | success | error | resent
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending]     = useState(false);

  // Extract ?token= from URL
  const token = new URLSearchParams(location.search).get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token found in this link. Please use the link from your email.');
      return;
    }

    authAPI.verifyEmail(token)
      .then(({ data }) => {
        if (data.accessToken) {
          setAccessToken(data.accessToken);
          updateUser(data.user);
        }
        setStatus('success');
        setMessage(data.message || 'Email verified successfully!');
        // Redirect to identity verification after 2.5s
        setTimeout(() => navigate('/verify-identity'), 2500);
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.response?.data?.message || 'This verification link is invalid or has expired.');
      });
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResending(true);
    try {
      await authAPI.resendVerification({ email: resendEmail });
      setStatus('resent');
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to send. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        {status === 'verifying' && (
          <>
            <Spinner size="lg" centered />
            <p style={{ ...s.sub, marginTop: 24, textAlign: 'center' }}>Verifying your email address...</p>
          </>
        )}

        {status === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <div style={s.icon}>✅</div>
            <h2 style={s.title}>Email Verified!</h2>
            <p style={s.sub}>{message}</p>
            <p style={{ ...s.sub, color: 'var(--gold)', marginTop: 12 }}>
              Redirecting to identity verification...
            </p>
          </div>
        )}

        {status === 'resent' && (
          <div style={{ textAlign: 'center' }}>
            <div style={s.icon}>📧</div>
            <h2 style={s.title}>Email Sent!</h2>
            <p style={s.sub}>A new verification link has been sent to <strong>{resendEmail}</strong>. Please check your inbox.</p>
            <Link to="/login" style={{ fontSize: 13, color: 'var(--gold)', marginTop: 20, display: 'block' }}>Back to Login</Link>
          </div>
        )}

        {status === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <div style={s.icon}>⚠️</div>
            <h2 style={s.title}>Link Expired</h2>
            <p style={s.sub}>{message}</p>

            <div style={{ borderTop: '1px solid var(--border)', marginTop: 28, paddingTop: 24 }}>
              <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
                Enter your email to receive a new verification link:
              </p>
              <form onSubmit={handleResend} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  className="input"
                  type="email"
                  placeholder="your@email.com"
                  value={resendEmail}
                  onChange={e => setResendEmail(e.target.value)}
                  style={{ flex: 1 }}
                  required
                />
                <button type="submit" className="btn btn-primary" disabled={resending}>
                  {resending ? 'Sending...' : 'Resend'}
                </button>
              </form>
            </div>

            <Link to="/login" style={{ fontSize: 12, color: 'var(--muted)', marginTop: 20, display: 'block' }}>
              Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page:  { minHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card:  { background: 'white', border: '1px solid var(--border)', padding: 48, maxWidth: 460, width: '100%' },
  icon:  { fontSize: 56, marginBottom: 20, display: 'block' },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontStyle: 'italic', marginBottom: 12 },
  sub:   { color: 'var(--muted)', fontSize: 13, lineHeight: 1.8 },
};
